import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Tooltip, IconButton } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { displayValue, downloadCsv, money, swilEnabled, swilRequest } from '../services/swil';
import './SwilDocuments.css';
import OutstandingFigures from './OutstandingFigures';
import { useCustomerInsights } from './CustomerInsightsContext';
import { outstandingSummary, isOverdueInvoice } from '../services/outstanding';
import DocumentLayout from './DocumentLayout';
import './DocumentLayout.css';
import { downloadDocument } from '../services/documentDownload';

const titles = { invoices: 'Invoices', 'credit-notes': 'Credit notes', outstanding: 'Outstanding', ledger: 'Ledger' };
function today(dayOffset = 0) {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function SwilLinks() {
  const { pathname } = useLocation();
  return <Stack direction="row" flexWrap="wrap" gap={1} my={2} className="swil-no-print report-tabs">
    <Button component={Link} to="/invoices" className={pathname === "/invoices" ? "report-tab-active" : ""} variant="outlined">Invoices</Button>
    <Button component={Link} to="/credit-notes" className={pathname === "/credit-notes" ? "report-tab-active" : ""} variant="outlined">Credit notes</Button>
    <Button component={Link} to="/ledger" className={pathname === "/ledger" ? "report-tab-active" : ""} variant="outlined">Ledger</Button>
    <Button component={Link} to="/account-statement" className={pathname === "/account-statement" ? "report-tab-active" : ""} variant="outlined">Outstanding</Button>
  </Stack>;
}

function ErrorNotice({ error }) {
  return <Alert severity={error.code === 'NOT_CONFIGURED' ? 'info' : 'error'}>{error.message}</Alert>;
}

function reportDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(String(value))) return displayValue(value);
  const date = new Date(String(value).slice(0, 10) + 'T00:00:00Z');
  return Number.isNaN(date.getTime()) ? displayValue(value) : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
}
const outstandingColumns = [
  ['FKInvoiceID', 'ID'], ['SalesInvNo', 'Invoice No'], ['SalesInvDate', 'SalesInvDate'],
  ['NetAmt', 'Net Amt'], ['Amt', 'Amt'], ['AdjustAmt', 'Adjusted'], ['DueAmt', 'Due Amt'],
  ['PDCAmt', 'PDC Amt'], ['DueDate', 'Due Date'],
];
export function DataTable({ rows, highlightOverdue = false }) {
  const [detail, setDetail] = useState(null);
  const columns = highlightOverdue ? outstandingColumns : Array.from(new Set(rows.flatMap(row => Object.keys(row)))).map(key => [key, key.replace(/_/g, ' ')]);
  const amountKeys = ['NetAmt', 'Amt', 'AdjustAmt', 'DueAmt', 'PDCAmt'];
  return <>
    <TableContainer className="responsive-report-table" component={Paper} variant="outlined" sx={{ my: 2, maxWidth: '100%' }}>
      <Table size="small"><TableHead><TableRow>{columns.map(([key, label]) => <TableCell key={key}>{label}</TableCell>)}{highlightOverdue && <TableCell>Action</TableCell>}</TableRow></TableHead>
        <TableBody>{rows.map((row, index) => <TableRow key={index} className={highlightOverdue && isOverdueInvoice(row) ? 'report-overdue-row' : ''}>{columns.map(([key, label]) => <TableCell key={key} data-label={label}>{highlightOverdue && amountKeys.includes(key) && row[key] != null ? money(row[key]) : highlightOverdue && ['SalesInvDate', 'DueDate'].includes(key) ? reportDate(row[key]) : displayValue(row[key])}</TableCell>)}{highlightOverdue && <TableCell data-label="Action"><Button onClick={() => setDetail(row)}>View Detail</Button></TableCell>}</TableRow>)}</TableBody>
      </Table>
    </TableContainer>
    <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="sm" aria-labelledby="outstanding-detail-title">
      <DialogTitle id="outstanding-detail-title">Outstanding detail {detail?.SalesInvNo}</DialogTitle>
      <DialogContent><dl className="report-detail-fields">{detail && Object.entries(detail).map(([key, value]) => <div key={key}><dt>{outstandingColumns.find(([field]) => field === key)?.[1] || key.replace(/_/g, ' ')}</dt><dd>{displayValue(value)}</dd></div>)}</dl></DialogContent>
      <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
    </Dialog>
  </>;
}

function DocumentDownload({ format, selected, customerId, from, to, kind }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [controller, setController] = useState(null);
  useEffect(() => () => controller?.abort(), [controller]);
  const download = async () => {
    const next = new AbortController(); setController(next); setBusy(true); setError('');
    try { await downloadDocument(kind === 'credit-notes' ? 'credit-note-detail' : 'invoice-detail', { customer_id: customerId, bill_no: selected, from, to }, format, next.signal); }
    catch (e) { if (!next.signal.aborted) setError(e.message); }
    finally { if (!next.signal.aborted) setBusy(false); }
  };
  return <span><Button disabled={busy} onClick={download} startIcon={busy ? <CircularProgress size={14} /> : <FileDownloadOutlined />} aria-label={`Download ${format.toUpperCase()} ${selected}`}>{busy ? 'Preparing…' : format.toUpperCase()}</Button>{error && <Alert severity="error">{error}</Alert>}</span>;
}

export function DocumentDialog({ selected, onClose, customerId, from, to, kind }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    if (!selected) return;
    const controller = new AbortController();
    setState({ loading: true });
    swilRequest(kind === 'credit-notes' ? 'credit-note-detail' : 'invoice-detail',
      { customer_id: customerId, bill_no: selected, from, to }, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setState({ data }); })
      .catch(error => { if (!controller.signal.aborted) setState({ error }); });
    return () => controller.abort();
  }, [selected, customerId, from, to, kind]);
  const data = state.data;
  return <Dialog className="document-dialog" open={Boolean(selected)} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>{kind === 'credit-notes' ? 'Credit note' : 'Invoice'} {selected}</DialogTitle>
    <DialogContent>
      {state.loading && <CircularProgress aria-label="Loading document" />}
      {state.error && <ErrorNotice error={state.error} />}
      {data && <DocumentLayout data={data} kind={kind} />}
    </DialogContent>
    <DialogActions className="swil-no-print">
      {data && ['pdf', 'csv'].map(format => <DocumentDownload key={`${selected}-${format}`} {...{ format, selected, customerId, from, to, kind }} />)}
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>;
}

function ListAction({ iconOnly = false, label, text, icon, ...props }) {
  return iconOnly ? <Tooltip title={label}><span><IconButton className="report-icon-action" aria-label={label} {...props}>{icon}</IconButton></span></Tooltip> : <Tooltip title={label}><span><Button aria-label={label} startIcon={icon} {...props}>{text || label}</Button></span></Tooltip>;
}

function ReportPdfDownload({ kind, customerId, range }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [controller, setController] = useState(null);
  useEffect(() => () => controller?.abort(), [controller]);
  const download = async () => {
    const next = new AbortController(); setController(next); setBusy(true); setError('');
    try { await downloadDocument(kind, { customer_id: customerId, ...range }, 'pdf', next.signal); }
    catch (failure) { if (!next.signal.aborted) setError(failure.message); }
    finally { if (!next.signal.aborted) setBusy(false); }
  };
  return <span><ListAction text={busy ? 'Preparing...' : 'PDF'} onClick={download} disabled={busy} icon={busy ? <CircularProgress size={18} /> : <FileDownloadOutlined />} label={busy ? 'Preparing PDF...' : `Download ${kind} PDF`} />{error && <Alert severity="error">{error}</Alert>}</span>;
}

export default function SwilDocuments({ kind, customer }) {
  const customerId = customer?.customer_id;
  const [from, setFrom] = useState(() => today(-6));
  const [to, setTo] = useState(() => today());
  const [range, setRange] = useState({ from, to });
  const [refresh, setRefresh] = useState(0);
  const [remoteState, setState] = useState({ loading: false });
  const insights = useCustomerInsights();
  const cached = insights?.balances[customerId];
  const usingCache = kind === 'outstanding' && Boolean(insights);
  const state = usingCache ? { rows: cached?.rows, loading: !cached?.rows && (!cached || ['queued','syncing'].includes(cached.status)), error: cached?.status === 'error' ? { message: 'Sync failed. Last saved balances are shown when available.' } : null } : remoteState;
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [validation, setValidation] = useState('');
  useEffect(() => {
    setSelected(null); setPage(0);
    if (usingCache) return;
    if (!customerId) { setState({}); return; }
    const controller = new AbortController();
    setState({ loading: true });
    swilRequest(kind, { customer_id: customerId, ...range }, { signal: controller.signal })
      .then(data => {
        if (!Array.isArray(data.rows)) throw new Error('The document service returned an unsupported response.');
        if (!controller.signal.aborted) setState({ rows: data.rows });
      })
      .catch(error => { if (!controller.signal.aborted) setState({ error }); });
    return () => controller.abort();
  }, [kind, customerId, range, refresh, usingCache]);
  const rows = state.rows || [];
  const apply = event => {
    event.preventDefault();
    if (!from || !to || from > to) { setValidation('Choose a valid date range.'); return; }
    setValidation(''); setRange({ from, to });
  };
  return <Box className="report-page" sx={{ maxWidth: 1200, mx: 'auto' }}>
    <div className="report-heading"><span className="module-icon teal"><ReceiptLongOutlined /></span><div><div className="eyebrow">SWIL · FINANCE & DOCUMENTS</div><h1>{titles[kind]}</h1><p>{customer?.customer_name || 'No customer selected'}</p></div></div>
    <SwilLinks />
    {!customerId ? <Alert severity="info">Select a customer from the salesman dashboard first.</Alert> : <>
      {kind !== 'outstanding' && <Stack component="form" onSubmit={apply} direction="row" gap={2} flexWrap="wrap" mb={2} className="swil-no-print report-filters">
        <TextField label="From date" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} required />
        <TextField label="To date" type="date" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} required />
        <Button type="submit" variant="contained" disabled={state.loading || !swilEnabled}>Apply dates</Button>
      </Stack>}
      {validation && <Alert severity="error">{validation}</Alert>}
      {state.loading && <Box py={4}><CircularProgress aria-label="Loading ERP records" /></Box>}
      {state.error && <ErrorNotice error={state.error} />}
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" className="report-actions swil-no-print">
        {!state.loading && swilEnabled && <ListAction iconOnly={['ledger', 'outstanding'].includes(kind)} label="Refresh list" icon={<RefreshRounded />} disabled={usingCache && ['queued','syncing'].includes(cached?.status)} onClick={() => usingCache ? insights.refreshCustomer(customerId) : setRefresh(value => value + 1)} />}
        {rows.length > 0 && <><span className="report-record-count">{rows.length.toLocaleString('en-IN')} records</span><ListAction text="CSV" icon={<FileDownloadOutlined />} onClick={() => downloadCsv(rows, `${kind}-${customerId}.csv`)} label={`Download ${{ invoices: 'invoice', 'credit-notes': 'credit note', ledger: 'ledger', outstanding: 'outstanding' }[kind]} list CSV`} />{['outstanding', 'ledger'].includes(kind) && <ReportPdfDownload key={`${kind}-${customerId}-${range.from}-${range.to}`} kind={kind} customerId={customerId} range={range} />}</>}
      </Stack>
      {kind === 'outstanding' && state.rows && <section className="outstanding-report-summary"><span>Outstanding balance</span><strong>{money(outstandingSummary(rows).amount)}</strong><OutstandingFigures stats={outstandingSummary(rows)} />{usingCache && cached?.updatedAt && <small className="balance-cache-note">Saved {new Date(cached.updatedAt).toLocaleString('en-IN')}{cached.stale ? ' · awaiting daily sync' : ''}</small>}</section>}
      {state.rows && <>
        {!rows.length ? <Alert severity="info">{kind === 'outstanding' ? 'No outstanding invoices returned for this customer.' : 'No documents found for these dates.'}</Alert> : <>

          {['outstanding', 'ledger'].includes(kind) ? <Box className="swil-print-area">
            <Typography variant="h6">S C Paul and Wholesale Private Limited</Typography>
            <Typography>{titles[kind]} — {customer?.customer_name} ({customerId})</Typography>
            <DataTable rows={rows.slice(page * 25, page * 25 + 25)} highlightOverdue={kind === 'outstanding'} /><TablePagination className="swil-no-print" component="div" count={rows.length} page={page} onPageChange={(_, value) => setPage(value)} rowsPerPage={25} rowsPerPageOptions={[25]} />
          </Box> : <>
            <TableContainer className="responsive-report-table" component={Paper} variant="outlined"><Table>
              <TableHead><TableRow><TableCell>Document number</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Details</TableCell><TableCell>PDF file</TableCell><TableCell>CSV file</TableCell></TableRow></TableHead>
              <TableBody>{rows.slice(page * 25, page * 25 + 25).map((row, index) => <TableRow className="document-summary-card" key={`${row.bill_no}-${index}`}>
                <TableCell data-label="Document number">{row.bill_no}</TableCell><TableCell data-label="Date">{displayValue(row.date)}</TableCell><TableCell data-label="Amount" align="right">{money(row.amount)}</TableCell>
                <TableCell className="document-card-action" data-label="Details"><Button startIcon={<VisibilityOutlined />} onClick={() => setSelected(row.bill_no)}>View</Button></TableCell>
                {['pdf', 'csv'].map(format => <TableCell className="document-card-action" key={format} data-label={`${format.toUpperCase()} file`}><DocumentDownload selected={row.bill_no} customerId={customerId} from={range.from} to={range.to} kind={kind} format={format} /></TableCell>)}
              </TableRow>)}</TableBody>
            </Table></TableContainer>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(_, value) => setPage(value)} rowsPerPage={25} rowsPerPageOptions={[25]} />
          </>}
        </>}
      </>}
    </>}
    <DocumentDialog selected={selected} onClose={() => setSelected(null)} customerId={customerId} from={range.from} to={range.to} kind={kind} />
  </Box>;
}

export function OrderInvoice({ orderId, customer }) {
  const [state, setState] = useState({});
  const [selected, setSelected] = useState(null);
  const [request, setRequest] = useState(0);
  const customerId = customer?.customer_id;
  useEffect(() => { setState({}); setSelected(null); setRequest(0); }, [orderId, customerId]);
  useEffect(() => {
    if (!request || !customerId) return;
    const controller = new AbortController();
    setState({ loading: true });
    swilRequest('order-invoice', { customer_id: customerId, order_id: orderId }, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setState({ rows: data.rows }); })
      .catch(error => { if (!controller.signal.aborted) setState({ error }); });
    return () => controller.abort();
  }, [request, orderId, customerId]);
  return <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
    <Typography variant="h6">Invoice for this order</Typography>
    {!swilEnabled ? <Alert severity="info">Invoice tracking will be available when S C Paul connects its ERP services.</Alert> :
      <Button disabled={!customerId || state.loading} onClick={() => setRequest(value => value + 1)}>Check invoice</Button>}
    {state.loading && <CircularProgress size={24} aria-label="Checking invoice" />}
    {state.error && <ErrorNotice error={state.error} />}
    {state.rows?.length === 0 && <Typography>No invoice has been returned for this order yet.</Typography>}
    {state.rows?.map(row => <Button key={row.bill_no} onClick={() => setSelected(row.bill_no)}>View invoice {row.bill_no}</Button>)}
    <DocumentDialog selected={selected} onClose={() => setSelected(null)} customerId={customerId} from="2000-01-01" to={today()} kind="invoices" />
  </Paper>;
}
